import { useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { IosInstallHint } from './IosInstallHint';
import { PwaTabBar } from './PwaTabBar';
import { PwaTrialExpired } from './PwaTrialExpired';
import { getTrialStatus } from './trial';

/**
 * Dedicated PWA shell with native iOS navigation patterns.
 *
 * Three-layer fixed layout (only the middle scrolls):
 *
 *   ┌─────────────────────────────────────────────┐ ← safe-area-top
 *   │  < Pack          Awaiting pack         ⋮    │  nav bar
 *   ├─────────────────────────────────────────────┤
 *   │                                             │
 *   │           scrollable page content           │
 *   │                                             │
 *   ├─────────────────────────────────────────────┤
 *   │  [Home]    [Pack]    [Returns]              │  tab bar
 *   └─────────────────────────────────────────────┘ ← safe-area-bottom
 *
 * The top nav bar is contextual per route (`navContext`) — different
 * pages get different titles + back labels. The bottom tab bar is
 * always visible so operators can jump workflows without back-tapping.
 *
 * Scan pages render their own sticky drawer ABOVE the tab bar using
 * `bottom: var(--pwa-tab-bar-h)` so the two stack rather than fight.
 */

interface NavContext {
  /** Center title for the top bar. Empty on home (logo only). */
  title: string;
  /** Back-button label, e.g. "Pack". Falsy on home (no back button). */
  backLabel: string | null;
  /** Where the back button navigates. Defaults to navigate(-1) when null. */
  backTo: string | null;
}

function resolveNav(pathname: string): NavContext {
  if (pathname === '/pwa') {
    return { title: '', backLabel: null, backTo: null };
  }
  if (pathname === '/pwa/pack') {
    return { title: 'Pack', backLabel: 'Home', backTo: '/pwa' };
  }
  if (pathname === '/pwa/returns') {
    return { title: 'Returns', backLabel: 'Home', backTo: '/pwa' };
  }
  if (pathname === '/pwa/profile') {
    return { title: 'Profile', backLabel: 'Home', backTo: '/pwa' };
  }
  if (/^\/pwa\/pack\/[^/]+/.test(pathname)) {
    return { title: 'Pack', backLabel: 'Pack', backTo: '/pwa/pack' };
  }
  if (/^\/pwa\/returns\/[^/]+/.test(pathname)) {
    return { title: 'Receive', backLabel: 'Returns', backTo: '/pwa/returns' };
  }
  return { title: 'Zafroi Ops', backLabel: 'Home', backTo: '/pwa' };
}

function makeInitials(input: string): string {
  const cleaned = input.trim();
  if (!cleaned) return '?';
  if (cleaned.includes('@')) {
    return cleaned[0]?.toUpperCase() ?? '?';
  }
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

export function PwaLayout() {
  const me = useCurrentUser();
  const navigate = useNavigate();
  const location = useLocation();
  const nav = useMemo(() => resolveNav(location.pathname), [location.pathname]);
  const onHome = location.pathname === '/pwa';
  const onProfile = location.pathname === '/pwa/profile';
  const trialExpired = useMemo(() => getTrialStatus().expired, []);
  const initials = makeInitials(me?.fullName ?? me?.email ?? '?');

  const onBack = () => {
    if (nav.backTo) navigate(nav.backTo);
    else navigate(-1);
  };

  return (
    <div
      className="pwa-screen pwa-no-highlight flex flex-col overflow-hidden bg-background"
      style={{ ['--pwa-tab-bar-h' as string]: '64px' }}
    >
      {/* ─── Top nav bar ──────────────────────────────────────────── */}
      <header className="pwa-chrome pwa-pt-safe pwa-pl-safe pwa-pr-safe shrink-0 border-b border-border/60 bg-card/90 backdrop-blur supports-[backdrop-filter]:bg-card/70">
        <div className="grid h-12 grid-cols-[1fr_auto_1fr] items-center px-1">
          <div className="flex items-center justify-start">
            {nav.backLabel ? (
              <button
                type="button"
                onClick={onBack}
                className="pwa-tap inline-flex h-10 items-center gap-0 rounded-full pl-1 pr-2.5 text-[15px] font-medium text-primary active:bg-primary/10"
                aria-label={`Back to ${nav.backLabel}`}
              >
                <ChevronLeft className="h-6 w-6 -ml-1" />
                <span className="ml-0.5 truncate max-w-[90px]">
                  {nav.backLabel}
                </span>
              </button>
            ) : (
              <BrandMark />
            )}
          </div>

          <h1 className="truncate text-center text-[15px] font-semibold tracking-tight text-foreground">
            {onHome ? '' : nav.title}
          </h1>

          <div className="flex items-center justify-end pr-1">
            <button
              type="button"
              onClick={() => navigate('/pwa/profile')}
              aria-label="Profile"
              aria-current={onProfile ? 'page' : undefined}
              className={`pwa-tap inline-flex h-9 w-9 items-center justify-center rounded-full text-[12px] font-semibold text-primary-foreground shadow-sm ${
                onProfile
                  ? 'bg-primary ring-2 ring-primary/30'
                  : 'bg-gradient-to-br from-primary to-[hsl(22_100%_45%)]'
              }`}
            >
              {initials}
            </button>
          </div>
        </div>
      </header>

      {/* ─── Scrollable content ──────────────────────────────────── */}
      <main
        className="pwa-pl-safe pwa-pr-safe relative flex-1 overflow-y-auto"
        style={{ paddingBottom: 'calc(var(--pwa-tab-bar-h) + env(safe-area-inset-bottom))' }}
      >
        <div className="px-4 pt-3">
          <IosInstallHint />
        </div>
        {trialExpired ? <PwaTrialExpired /> : <Outlet />}
      </main>

      <PwaTabBar />
    </div>
  );
}

/**
 * Brand mark for the top-left of the home screen — small saffron Zi
 * tile + "Zafroi Ops" wordmark in the platform typeface. Replaces
 * the plain centred "Zafroi Ops" title that felt unbranded.
 */
function BrandMark() {
  return (
    <div className="flex items-center gap-2 pl-2">
      <img
        src="/brand/favicon.svg"
        alt=""
        className="h-7 w-7 rounded-md"
        aria-hidden
      />
      <span className="text-[14px] font-semibold tracking-tight text-foreground">
        Zafroi <span className="text-primary">Ops</span>
      </span>
    </div>
  );
}
